import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Loader2, Search, RotateCcw, CheckCircle2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { refundsApi, type RefundResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/refunds")({
  component: RefundsPage,
});

const statusColors: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  rejected: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
  processed: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
};

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" });
}

function RefundsPage() {
  const [refunds, setRefunds] = useState<RefundResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [processingId, setProcessingId] = useState<string | null>(null);

  const fetchRefunds = async () => {
    setLoading(true);
    try {
      const res = await refundsApi.list({ status: statusFilter || undefined, pageSize: 100 });
      setRefunds(res.data);
    } catch {
      toast.error("Failed to load refunds");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefunds();
  }, [statusFilter]);

  const handleProcess = async (id: string, status: "approved" | "rejected") => {
    setProcessingId(id);
    try {
      await refundsApi.process(id, { status });
      toast.success(`Refund ${status}`);
      fetchRefunds();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Update failed";
      toast.error(msg);
    } finally {
      setProcessingId(null);
    }
  };

  const filtered = refunds.filter((r) => {
    const s = search.toLowerCase();
    return !s || r.refundRef.toLowerCase().includes(s) || r.bookingRef.toLowerCase().includes(s) || r.reason.toLowerCase().includes(s);
  });

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Refunds</h1>
          <p className="text-sm text-muted-foreground">Process and manage refund requests.</p>
        </div>
        <Badge variant="secondary" className="gap-1.5">
          <RotateCcw className="size-3" /> {refunds.filter((r) => r.status === "pending").length} pending
        </Badge>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search refunds..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-64" />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="processed">Processed</option>
        </select>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/60 bg-card py-20 text-center shadow-soft">
          <RotateCcw className="mb-3 size-12 text-muted-foreground/30" />
          <p className="font-semibold text-muted-foreground">No refunds found</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border/60 bg-card shadow-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Refund / Booking</th>
                <th className="px-4 py-3 text-left font-medium">Recipient</th>
                <th className="px-4 py-3 text-left font-medium">Payout Account</th>
                <th className="px-4 py-3 text-left font-medium">Amount</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Date</th>
                <th className="px-4 py-3 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-border/60 hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.refundRef}</div>
                    <div className="font-mono text-xs text-muted-foreground">{r.bookingRef}</div>
                    <div className="text-xs text-muted-foreground max-w-xs truncate" title={r.reason}>{r.reason}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.userName || "Guest"}</div>
                    <div className="text-xs text-muted-foreground">{r.userEmail}</div>
                    <div className="text-xs text-muted-foreground">{r.userPhone}</div>
                  </td>
                  <td className="px-4 py-3">
                    {r.bankName ? (
                      <>
                        <div className="font-medium">{r.bankName}</div>
                        <div className="font-mono text-xs">{r.accountNumber}</div>
                        <div className="text-xs text-muted-foreground">{r.accountName}</div>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">No account provided</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-semibold">{formatNaira(r.amount)}</td>
                  <td className="px-4 py-3">
                    <Badge className={`text-xs capitalize ${statusColors[r.status] || ""}`}>{r.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(r.createdAt)}</td>
                  <td className="px-4 py-3">
                    {processingId === r.id ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : r.status === "pending" ? (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="h-8 gap-1 text-emerald-600" onClick={() => handleProcess(r.id, "approved")}>
                          <CheckCircle2 className="size-3.5" /> Paid out
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 gap-1 text-red-600" onClick={() => handleProcess(r.id, "rejected")}>
                          <XCircle className="size-3.5" /> Reject
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
